import { IListProps, IPageProps, IPageSpecification, IRectangle } from '@fluentui/react';
import { IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IPList } from "baseComponents/List/IPList";

interface IInfiniteListProps extends IListProps {
  hasMoreData: boolean;
  pageSize?: number;
  listHeight?: number;
  onFetchMore: () => Promise<void>;
  reRender?: string;
}

const InfiniteList = (props: IInfiniteListProps) => {
  const { t } = useTranslation('translation');
  const [isLoading, setIsLoading] = useState(false);
  const lastItemsLengthRef = useRef<number>(0);
  const lastFetchTimeRef = useRef<number>(0);
  const shouldFetchMoreRef = useRef<boolean>(false);

  // Destructure props for use in useEffect dependencies
  const { hasMoreData, onFetchMore, items, pageSize } = props;

  // Use a callback to fetch more data
  const fetchMoreData = useCallback(async () => {
    // Prevent multiple fetches within a short time (throttle)
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    if (
      shouldFetchMoreRef.current &&
      hasMoreData &&
      !isLoading &&
      timeSinceLastFetch > 300 // Throttle to prevent multiple requests
    ) {
      try {
        setIsLoading(true);
        lastFetchTimeRef.current = now;
        await onFetchMore();
      } catch (err) {
        console.error('Error fetching more data:', err);
      } finally {
        setIsLoading(false);
        shouldFetchMoreRef.current = false;
      }
    }
  }, [hasMoreData, onFetchMore, isLoading]);

  // Use an effect to fetch more data when needed
  useEffect(() => {
    fetchMoreData();
  }, [fetchMoreData]);

  // Track when items change and set flag if we need more data
  useEffect(() => {
    const currentLength = items?.length || 0;

    // If we're at the bottom of the list and have more data to fetch, set the flag
    if (hasMoreData && !isLoading && currentLength > 0 && currentLength === lastItemsLengthRef.current) {
      shouldFetchMoreRef.current = true;
    }

    lastItemsLengthRef.current = currentLength;
  }, [items, hasMoreData, isLoading]);

  const onRenderPage = (
    pageProps?: IPageProps,
    defaultRender?: (props?: IPageProps) => JSX.Element | null,
  ): JSX.Element | null => {
    const page = pageProps?.page;
    if (
      hasMoreData &&
      !isLoading &&
      page &&
      page.isSpacer === false &&
      page.isVisible === true &&
      page.startIndex + page.itemCount >= (items?.length ?? 0)
    ) {
      // Instead of fetching directly, mark that we should fetch
      shouldFetchMoreRef.current = true;
    }

    if (defaultRender) {
      return defaultRender(pageProps);
    } else {
      return null;
    }
  };

  const onGetPageSpecification = (
    itemIndex?: number | undefined,
    visibleRect?: IRectangle | undefined,
  ): IPageSpecification => {
    return {
      itemCount: pageSize ?? 10,
      height: props.listHeight,
    };
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow>
        <IPList
          {...props}
          onRenderPage={onRenderPage}
          onShouldVirtualize={() => {
            return true;
          }}
          getPageSpecification={onGetPageSpecification}
          version={props.reRender}
          usePageCache={true}
        />
      </IPStack.Item>
      {isLoading && (
        <IPStack horizontalAlign="center">
          <IPSpinner />
        </IPStack>
      )}
      {items && items.length > (pageSize ?? 10) && !hasMoreData && (
        <IPStack horizontalAlign="center">
          <IPText styles={globalTextStylesBold}>{t('translation:General.Notifications.NoMoreItems')}</IPText>
        </IPStack>
      )}
    </IPStack>
  );
};

export default InfiniteList;
